import { Checkbox, Col, Row } from 'antd';
import { BaseSelect } from 'components/UI/BaseSelect';
import { useMachineFilter } from 'hooks/useMachineFilter';
import PropType from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { NOTIFICATION_SEVERITY, TYPES } from '../util/constant';

export const NotificationFilter = ({ filters, onChange }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.NOTIFICATION);
  const { Option } = BaseSelect;
  const { severity, type, ...machineFilter } = filters;
  const [MachineFilter, machineFilterState] = useMachineFilter(machineFilter);
  const [notificationFilterState, setNotificationFilterState] =
    useState(filters);

  const severityCheckboxOptions = useMemo(
    () =>
      Object.keys(NOTIFICATION_SEVERITY).map((notificationSeverity) => ({
        label: t(`severityNameMapping.${notificationSeverity}`),
        value: notificationSeverity
      })),
    [t]
  );

  const handleChange = useCallback(
    (property, value) => {
      const updatedState = {
        ...notificationFilterState,
        [property]: value || undefined
      };
      setNotificationFilterState(updatedState);
      onChange(updatedState);
    },
    [notificationFilterState, onChange]
  );

  const handleNotificationTypeChange = useCallback(
    (value) => handleChange('type', value),
    [handleChange]
  );

  const handleNotificationSeverityChange = useCallback(
    (value) => handleChange('severity', value),
    [handleChange]
  );

  useEffect(() => {
    if (
      machineFilterState.facility !== notificationFilterState.facility ||
      machineFilterState.object !== notificationFilterState.object ||
      machineFilterState.machineId !== notificationFilterState.machineId
    ) {
      const newState = { ...notificationFilterState, ...machineFilterState };
      setNotificationFilterState(newState);
      onChange(newState);
    }
  }, [notificationFilterState, machineFilterState, onChange]);

  return (
    <div className="notifications-filters">
      <Row gutter={[12, 12]}>
        <Col>
          <MachineFilter defaultState={machineFilter} />
        </Col>
        <Col>
          <h5 className="filtering-label">{t('filter.notificationType')}</h5>
          <BaseSelect
            className="custom-select min-w-180"
            defaultValue={notificationFilterState.type}
            dropdownMatchSelectWidth={false}
            onChange={handleNotificationTypeChange}>
            <Option value="">{t('filter.selectNotificationType')}</Option>
            {Object.keys(TYPES).map((key) => (
              <Option key={TYPES[key].id} value={TYPES[key].id}>
                {t(`notificationTypeMapping.${key}`)}
              </Option>
            ))}
          </BaseSelect>
        </Col>
      </Row>
      <Row className="mt-8">
        <Col>
          <h5 className="filtering-label">{t('filter.severity')}</h5>
          <div className="notification-severity">
            <Checkbox.Group
              defaultValue={severity}
              options={severityCheckboxOptions}
              onChange={handleNotificationSeverityChange}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

NotificationFilter.propTypes = {
  filters: PropType.shape().isRequired,
  onChange: PropType.func.isRequired
};
